import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useContext, useMemo } from 'react';
import { ANONYMOUS_AUTH, OidcAuthContext } from './auth-context';
import { fetchCurrentUser, primaryProfile, setUserPassword, setUserPhone, resendUserEmailVerification, updateUser } from '../clients/zitadel-gql';
import { useNotificationsContext } from './notifications';
const FALLBACK_PASSWORD_POLICY = {
    minLength: 8,
    hasUppercase: true,
    hasLowercase: true,
    hasNumber: true,
    hasSymbol: false,
    isDefault: true
};
const toAuthUser = (user) => {
    const profile = primaryProfile(user);
    const preferredLanguage = user.preferences?.preferredLanguage ?? profile?.preferredLanguage ?? '';
    return {
        id: user.id,
        state: user.state,
        userName: user.userName,
        loginNames: user.loginNames,
        preferredLoginName: user.preferredLoginName,
        human: {
            profile: {
                firstName: profile?.firstName ?? '',
                lastName: profile?.lastName ?? '',
                nickName: profile?.nickName ?? '',
                displayName: profile?.displayName ?? user.userName,
                preferredLanguage,
                gender: profile?.gender ?? '',
                avatarUrl: profile?.avatarUrl ?? ''
            },
            email: {
                email: profile?.email ?? '',
                isEmailVerified: Boolean(profile?.email)
            },
            phone: {
                phone: profile?.phone ?? '',
                isPhoneVerified: Boolean(profile?.phone)
            }
        }
    };
};
const AuthUserContext = createContext(null);
export const AuthUserProvider = ({ children }) => {
    const auth = useContext(OidcAuthContext) ?? ANONYMOUS_AUTH;
    const baseUrl = auth.settings.authority;
    const notify = useNotificationsContext();
    const [data, setData] = React.useState();
    const accessToken = auth.user?.access_token;
    const sendRestRequest = async (path, method, body, headers = {}, options = { stringifyBody: true }) => {
        try {
            const reqHeaders = Object.fromEntries(Object.entries({
                Accept: 'application/json',
                Authorization: `Bearer ${accessToken}`,
                ...headers
            }).filter(([_, value]) => value !== undefined));
            const reqBody = options.stringifyBody ? JSON.stringify(body) : body;
            const response = await fetch(`${baseUrl}${path}`, {
                method,
                headers: reqHeaders,
                body: reqBody
            });
            if (!response.ok) {
                let message = response.statusText;
                if (response.headers.get('content-type') === 'application/json') {
                    const errorData = await response.json().catch(() => undefined);
                    message = errorData?.message || message;
                }
                if (!options.silent) {
                    notify.toast({
                        position: 'top-right',
                        title: 'Error',
                        description: message,
                        status: 'error'
                    });
                }
                return { ok: false, status: response.status };
            }
            if (!options.silent) {
                notify.toast({
                    position: 'top-right',
                    title: 'Success',
                    description: 'Action completed successfully',
                    status: 'success'
                });
            }
            await refetchProfile();
            return { ok: true, status: response.status };
        }
        catch (error) {
            if (!options.silent) {
                notify.toast({
                    position: 'top-right',
                    title: 'Error',
                    description: `Internal error: ${error.message}`,
                    status: 'error'
                });
            }
            return { ok: false };
        }
    };
    const runGqlAction = async (action) => {
        try {
            const result = await action();
            if (!result.ok) {
                notify.toast({
                    position: 'top-right',
                    title: 'Error',
                    description: result.message || 'Action failed',
                    status: 'error'
                });
                return false;
            }
            notify.toast({
                position: 'top-right',
                title: 'Success',
                description: 'Action completed successfully',
                status: 'success'
            });
            await refetchProfile();
            return true;
        }
        catch (error) {
            notify.toast({
                position: 'top-right',
                title: 'Error',
                description: error.message,
                status: 'error'
            });
            return false;
        }
    };
    const getPasswordPolicy = async () => {
        try {
            const response = await fetch(`${baseUrl}/auth/v1/policies/passwords/complexity`, {
                method: 'GET',
                headers: {
                    Accept: 'application/json',
                    Authorization: `Bearer ${accessToken}`
                }
            });
            if (!response.ok)
                return FALLBACK_PASSWORD_POLICY;
            const policyData = await response.json();
            return policyData.policy ?? FALLBACK_PASSWORD_POLICY;
        }
        catch {
            return FALLBACK_PASSWORD_POLICY;
        }
    };
    const refetchProfile = async () => {
        if (!accessToken)
            return;
        const [user, passwordPolicy] = await Promise.all([
            fetchCurrentUser(accessToken).then(toAuthUser),
            getPasswordPolicy()
        ]);
        setData({ user, passwordPolicy });
    };
    React.useEffect(() => {
        const fetchProfile = async () => {
            if (!auth.isAuthenticated)
                return;
            try {
                await refetchProfile();
            }
            catch (error) {
                console.error('jaen: loading the current user failed', error);
            }
        };
        void fetchProfile();
    }, [auth.isAuthenticated]);
    const requireUserId = () => {
        const userId = data?.user?.id;
        if (!userId) {
            throw new Error('The current user is not loaded yet');
        }
        return userId;
    };
    const applyChanges = async (changes) => {
        await runGqlAction(() => updateUser({
            accessToken: accessToken,
            userId: requireUserId(),
            changes
        }));
    };
    const usernameUpdate = async (userName) => {
        await applyChanges({ username: userName });
    };
    const profileUpdate = async (profile) => {
        const nonEmpty = (value) => value?.trim() || undefined;
        await applyChanges({
            profile: {
                givenName: nonEmpty(profile.firstName),
                familyName: nonEmpty(profile.lastName),
                displayName: nonEmpty(profile.displayName),
                preferredLanguage: nonEmpty(profile.preferredLanguage),
                nickName: nonEmpty(profile.nickName),
                gender: profile.gender || 'GENDER_UNSPECIFIED'
            }
        });
    };
    const profileAvatarUpdate = async (avatarFile) => {
        const formData = new FormData();
        formData.append('file', avatarFile);
        await sendRestRequest('/assets/v1/users/me/avatar', 'POST', formData, {
            Accept: undefined
        }, {
            stringifyBody: false
        });
    };
    const emailUpdate = async (email) => {
        await applyChanges({ email: { email } });
    };
    const emailResendCode = async () => {
        await runGqlAction(() => resendUserEmailVerification({
            accessToken: accessToken,
            userId: requireUserId()
        }));
    };
    const phoneUpdate = async (phone) => {
        await runGqlAction(() => setUserPhone({
            accessToken: accessToken,
            userId: requireUserId(),
            phone
        }));
    };
    const phoneVerify = async (code) => {
        await sendRestRequest('/auth/v1/users/me/phone/_verify', 'POST', { code });
    };
    const phoneResendCode = async () => {
        await sendRestRequest('/auth/v1/users/me/phone/_resend_verification', 'POST', {});
    };
    const phoneDelete = async () => {
        await sendRestRequest('/auth/v1/users/me/phone', 'DELETE', {});
    };
    const passwordUpdate = async (oldPassword, newPassword) => {
        const rest = await sendRestRequest('/auth/v1/users/me/password', 'PUT', {
            oldPassword,
            newPassword
        }, {}, { stringifyBody: true, silent: true });
        if (rest.ok) {
            notify.toast({
                position: 'top-right',
                title: 'Success',
                description: 'Action completed successfully',
                status: 'success'
            });
            return;
        }
        const routeMissing = rest.status === undefined || [404, 405, 501].includes(rest.status);
        if (!routeMissing) {
            notify.toast({
                position: 'top-right',
                title: 'Error',
                description: rest.status === 400 || rest.status === 403
                    ? 'The current password is not correct.'
                    : 'The password could not be changed.',
                status: 'error'
            });
            return;
        }
        await runGqlAction(() => setUserPassword({
            accessToken: accessToken,
            userId: requireUserId(),
            newPassword,
            changeRequired: false
        }));
    };
    const refresh = async () => {
        await refetchProfile();
    };
    const value = useMemo(() => {
        return {
            user: data?.user,
            passwordPolicy: data?.passwordPolicy,
            usernameUpdate,
            profileUpdate,
            profileAvatarUpdate,
            emailUpdate,
            emailResendCode,
            phoneUpdate,
            phoneVerify,
            phoneResendCode,
            phoneDelete,
            passwordUpdate,
            refresh
        };
    }, [data]);
    return (_jsx(AuthUserContext.Provider, { value: value, children: children }));
};
export const useAuthUser = () => {
    const context = React.useContext(AuthUserContext);
    if (!context) {
        throw new Error('useAuthUser must be used within a AuthUserProvider');
    }
    return context;
};
